import { useCallback, useEffect, useRef, useState } from 'react';
import { getSportsbookHistoryErrorMessage, requestSportsbookHistoryDetail } from '../api/sportsbookHistoryRequest.api.js';
import {
  createSportsbookHistoryDetailCache,
  sportsbookHistoryDetailCacheKey,
} from './sportsbookHistoryDetailCache.detail.js';

const IDLE_DETAIL_STATE = Object.freeze({ status: 'idle', detail: null, error: '' });

export const useSportsbookHistoryDetail = ({ service, rows, onVersionMismatch }) => {
  const [states, setStates] = useState({});
  const mountedRef = useRef(true);
  const rowsRef = useRef(rows);
  const operationsRef = useRef(new Map());
  rowsRef.current = rows;
  const serviceRef = useRef(service);
  const versionMismatchRef = useRef(onVersionMismatch);
  serviceRef.current = service;
  versionMismatchRef.current = onVersionMismatch;

  const cacheRef = useRef(null);
  if (!cacheRef.current) {
    cacheRef.current = createSportsbookHistoryDetailCache({
      requestDetail: (historyId, signal) => requestSportsbookHistoryDetail({
        service: serviceRef.current,
        historyId,
        signal,
      }),
    });
  }

  useEffect(() => {
    cacheRef.current.syncRows(rows);
    const validKeys = new Map(rows.map((row) => [row.history_id, sportsbookHistoryDetailCacheKey(row)]));
    for (const [historyId, operation] of operationsRef.current) {
      if (validKeys.get(historyId) !== operation.key) operationsRef.current.delete(historyId);
    }
    setStates((current) => {
      const next = Object.fromEntries(Object.entries(current).filter(([historyId, state]) => validKeys.get(historyId) === state.key));
      return Object.keys(next).length === Object.keys(current).length ? current : next;
    });
  }, [rows]);

  useEffect(() => {
    const operations = operationsRef.current;
    const cache = cacheRef.current;
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      operations.clear();
      cache.abortAll();
    };
  }, []);

  const load = useCallback(async (row, { force = false } = {}) => {
    const requestKey = sportsbookHistoryDetailCacheKey(row);
    if (!mountedRef.current) return null;
    const cached = cacheRef.current.peek(row);
    if (cached && !force) return cached;
    const previousOperation = operationsRef.current.get(row.history_id);
    const operation = !force && previousOperation?.key === requestKey ? previousOperation : { key: requestKey };
    operationsRef.current.set(row.history_id, operation);
    const isCurrent = () => mountedRef.current && operationsRef.current.get(row.history_id) === operation;
    setStates((current) => isCurrent() ? ({
      ...current,
      [row.history_id]: { key: requestKey, status: 'loading', detail: null, error: '' },
    }) : current);
    try {
      const detail = await cacheRef.current.load(row, { force });
      if (!detail || !isCurrent()) return null;
      const responseKey = sportsbookHistoryDetailCacheKey(detail);
      setStates((current) => isCurrent() ? ({
        ...current,
        [row.history_id]: { key: responseKey, status: 'ready', detail, error: '' },
      }) : current);
      if (responseKey !== requestKey) versionMismatchRef.current?.();
      return detail;
    } catch (error) {
      if (!isCurrent()) return null;
      if (error?.name === 'AbortError') return null;
      if (error?.code === 'SPORTSBOOK_HISTORY_DETAIL_VERSION_MISMATCH') {
        versionMismatchRef.current?.();
      }
      setStates((current) => {
        if (!isCurrent() || current[row.history_id]?.key !== requestKey) return current;
        return {
          ...current,
          [row.history_id]: {
            key: requestKey,
            status: 'error',
            detail: null,
            error: getSportsbookHistoryErrorMessage(error, '상세 내역을 불러오지 못했습니다.'),
          },
        };
      });
      return null;
    }
  }, []);

  const getState = useCallback((row) => {
    if (row === null || typeof row !== 'object' || Array.isArray(row)) return IDLE_DETAIL_STATE;
    const key = sportsbookHistoryDetailCacheKey(row);
    const cached = cacheRef.current.peek(row);
    if (cached) return { status: 'ready', detail: cached, error: '' };
    const state = states[row.history_id];
    return state?.key === key ? state : IDLE_DETAIL_STATE;
  }, [states]);

  const acceptAuthoritativeDetail = useCallback((detail) => {
    if (!mountedRef.current) return false;
    const row = rowsRef.current.find((candidate) => candidate.history_id === detail?.history_id);
    if (!row || !cacheRef.current.acceptAuthoritativeDetail(row, detail)) return false;
    const operation = { key: sportsbookHistoryDetailCacheKey(detail) };
    operationsRef.current.set(detail.history_id, operation);
    setStates((current) => (
      mountedRef.current && operationsRef.current.get(detail.history_id) === operation
        ? { ...current, [detail.history_id]: { key: operation.key, status: 'ready', detail, error: '' } }
        : current
    ));
    return true;
  }, []);

  return {
    load,
    acceptAuthoritativeDetail,
    refresh: useCallback((row) => load(row, { force: true }), [load]),
    getState,
    invalidate: useCallback((historyId) => {
      operationsRef.current.delete(historyId);
      cacheRef.current.invalidate(historyId);
    }, []),
  };
};
